import { de } from "date-fns/locale";
import auth from "../../services/auth";
import authHelper from "../../services/auth-helper";
import ModalValidate from "../../components/modal-validate/modal-validate.vue";

export default {
  components: {
    ModalValidate,
  },

  data() {
    return {
      taxIdNumber: undefined,
      formState: "NEW",
      loading: false,
      validTaxIdNumber: false,
      origemTelas: undefined,
      errorMessage: undefined,
      initProcess: false,
      showValidationModal: false,
      userEmail: "",
      userResponse: null,
      initialTimerValue: 60,
      isProcessing: false,
      requestTimeout: 30000,
      pendingMoveMaisId: null,
      selectedChannel: null,
    };
  },

  computed: {
    type() {
      return this.$route.params.type;
    },
  },

  created() {
    this.taxIdNumber = this.$route.query.taxIdNumber;
    this.origemTelas = this.$route.params.type;
  },

  mounted() {
    // Verifica se há um timer ativo e dados salvos para restaurar o fluxo
    this.restorePasswordResetSession();
  },

  methods: {
    /**
     * Obtém o moveMaisId do userResponse de forma segura
     * Suporta tanto o formato onde moveMaisId está na raiz quanto dentro de user
     */
    getMoveMaisIdFromResponse(response) {
      if (!response) {
        return null;
      }
      // Tenta obter da raiz primeiro (formato atual da API)
      if (response.moveMaisId !== undefined) {
        return response.moveMaisId;
      }
      // Tenta obter de user (formato antigo)
      if (response.user && response.user.moveMaisId !== undefined) {
        return response.user.moveMaisId;
      }
      return null;
    },

    validateTaxIdNumber(taxIdNumber) {
      if (!taxIdNumber || typeof taxIdNumber !== "string") {
        return false;
      }
      const cleaned = taxIdNumber.replace(/[.\-\/]/g, "").toUpperCase();
      return cleaned.length === 11 || cleaned.length === 14;
    },

    sanitizeInput(input) {
      if (typeof input !== "string") {
        return "";
      }
      return input.trim().replace(/[<>]/g, "");
    },

    doRequest() {
      if (this.isProcessing) {
        return;
      }

      if (!this.taxIdNumber || this.taxIdNumber === "") {
        this.formState = "ERROR";
        this.errorMessage = "CPF/CNPJ Não informado";
        this.initProcess = false;
        return;
      }

      const sanitizedTaxId = this.sanitizeInput(this.taxIdNumber);
      const cpfSemMascara = sanitizedTaxId.replace(/[.\-\/]/g, "").toUpperCase();

      if (!this.validateTaxIdNumber(sanitizedTaxId)) {
        this.formState = "ERROR";
        this.errorMessage = "CPF/CNPJ inválido";
        this.initProcess = false;
        return;
      }

      if (cpfSemMascara.length < 11 || cpfSemMascara.length > 14) {
        this.formState = "ERROR";
        this.errorMessage = "CPF/CNPJ deve ter 11 ou 14 dígitos";
        this.initProcess = false;
        return;
      }

      this.$validator.validateAll().then((success) => {
        if (success) {
          this.fetchUserDataAndSendCode();
        } else {
          this.initProcess = false;
        }
      });
    },

    fetchUserDataAndSendCode() {
      if (this.isProcessing) {
        return;
      }

      this.isProcessing = true;
      const cpfSemMascara = this.taxIdNumber.replace(/[.\-\/]/g, "").toUpperCase();

      if (!cpfSemMascara || cpfSemMascara.length < 11) {
        this.formState = "ERROR";
        this.errorMessage = "CPF/CNPJ inválido";
        this.initProcess = false;
        this.isProcessing = false;
        return;
      }

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(
            new Error("Tempo de requisição excedido. Verifique sua conexão.")
          );
        }, this.requestTimeout);
      });

      // O backend gera o JWT automaticamente usando o CPF do parâmetro
      Promise.race([
        authHelper.getUserAndAddressByTaxIdNumberNoSession(cpfSemMascara),
        timeoutPromise,
      ])
        .then((response) => {
          if (!response || typeof response !== "object") {
            throw new Error("Resposta inválida do servidor");
          }

          // Normaliza a resposta para sempre ter a estrutura user
          // A API pode retornar moveMaisId na raiz ou dentro de user
          let moveMaisId = null;
          if (response.moveMaisId !== undefined) {
            // Formato atual: moveMaisId na raiz
            moveMaisId = response.moveMaisId;
            // Cria estrutura user para compatibilidade com o resto do código
            this.userResponse = {
              ...response,
              user: {
                moveMaisId: response.moveMaisId,
                taxIdNumber: response.taxIdNumber,
                flag2FA: (response.user && response.user.flag2FA) || "N", // Default para "N" se não presente
                ...(response.user || {}),
              },
            };
          } else if (response.user && response.user.moveMaisId !== undefined) {
            // Formato antigo: moveMaisId dentro de user
            moveMaisId = response.user.moveMaisId;
            // Garante que flag2FA tenha um valor padrão
            if (!response.user.flag2FA) {
              response.user.flag2FA = "N";
            }
            this.userResponse = response;
          } else {
            throw new Error("Dados do usuário não encontrados na resposta");
          }

          if (
            !moveMaisId ||
            (typeof moveMaisId !== "number" && typeof moveMaisId !== "string")
          ) {
            this.formState = "ERROR";
            this.errorMessage = "Erro ao processar dados do usuário.";
            this.initProcess = false;
            this.isProcessing = false;
            return;
          }

          const moveMaisIdNum = Number(moveMaisId);
          if (isNaN(moveMaisIdNum) || moveMaisIdNum <= 0) {
            this.formState = "ERROR";
            this.errorMessage = "ID do usuário inválido.";
            this.initProcess = false;
            this.isProcessing = false;
            return;
          }

          // Salva o moveMaisId para uso posterior quando o canal for selecionado
          this.pendingMoveMaisId = moveMaisIdNum;

          // Salva a sessão
          this.savePasswordResetSession();

          // Mostra o modal para seleção de canal (não gera token ainda)
          this.showValidationModal = true;
          this.initProcess = false;
          this.isProcessing = false;
        })
        .catch((err) => {
          this.isProcessing = false;

          if (err.message && err.message.includes("Tempo de requisição")) {
            this.formState = "ERROR";
            this.errorMessage =
              "Tempo de requisição excedido. Verifique sua conexão e tente novamente.";
            this.initProcess = false;
            return;
          }

          if (err.code === "ECONNABORTED" || err.message === "Network Error") {
            this.formState = "ERROR";
            this.errorMessage =
              "Erro de conexão. Verifique sua internet e tente novamente.";
            this.initProcess = false;
            return;
          }

          const errorCode =
            err.response &&
            err.response.data &&
            err.response.data.status &&
            err.response.data.status.code;

          if (errorCode === 9990) {
            const remainingTime = this.getRemainingTimer();
            this.initialTimerValue = remainingTime > 0 ? remainingTime : 0;

            // Se temos userResponse, salva o moveMaisId para reenvio
            if (this.userResponse) {
              const moveMaisId = this.getMoveMaisIdFromResponse(
                this.userResponse
              );
              if (moveMaisId) {
                const moveMaisIdNum = Number(moveMaisId);
                if (!isNaN(moveMaisIdNum) && moveMaisIdNum > 0) {
                  this.pendingMoveMaisId = moveMaisIdNum;
                }
              }
            }

            this.showValidationModal = true;
            this.initProcess = false;
          } else {
            this.formState = "ERROR";
            this.errorMessage =
              (err.response &&
                err.response.data &&
                err.response.data.status &&
                err.response.data.status.message) ||
              "Erro ao enviar código. Tente novamente.";
            this.initProcess = false;
          }
        });
    },

    saveTokenTimestamp() {
      try {
        const timestamp = Date.now();
        if (isNaN(timestamp) || timestamp <= 0) {
          return;
        }
        localStorage.setItem(
          "passwordResetTokenTimestamp",
          timestamp.toString()
        );
      } catch (err) {
        return;
      }
    },

    /**
     * Salva os dados da sessão de reset de senha no localStorage
     */
    savePasswordResetSession() {
      try {
        const sessionData = {
          taxIdNumber: this.taxIdNumber,
          pendingMoveMaisId: this.pendingMoveMaisId,
          selectedChannel: this.selectedChannel,
          userResponse: this.userResponse,
        };
        localStorage.setItem(
          "passwordResetSession",
          JSON.stringify(sessionData)
        );
      } catch (err) {
        console.error("Erro ao salvar sessão de reset de senha:", err);
      }
    },

    /**
     * Recupera os dados da sessão de reset de senha do localStorage
     */
    getPasswordResetSession() {
      try {
        const sessionData = localStorage.getItem("passwordResetSession");
        if (!sessionData) {
          return null;
        }
        return JSON.parse(sessionData);
      } catch (err) {
        console.error("Erro ao recuperar sessão de reset de senha:", err);
        return null;
      }
    },

    /**
     * Limpa os dados da sessão de reset de senha do localStorage
     */
    clearPasswordResetSession() {
      try {
        localStorage.removeItem("passwordResetSession");
        localStorage.removeItem("passwordResetTokenTimestamp");
      } catch (err) {
        console.error("Erro ao limpar sessão de reset de senha:", err);
      }
    },

    /**
     * Restaura a sessão de reset de senha se houver timer ativo
     */
    async restorePasswordResetSession() {
      // Recupera os dados salvos
      const sessionData = this.getPasswordResetSession();
      if (!sessionData || !sessionData.pendingMoveMaisId) {
        // Se não há dados salvos, limpa tudo e não faz nada
        this.clearPasswordResetSession();
        return;
      }

      // Restaura os dados básicos
      this.taxIdNumber = sessionData.taxIdNumber || this.taxIdNumber;
      this.pendingMoveMaisId = sessionData.pendingMoveMaisId;
      this.selectedChannel = sessionData.selectedChannel;
      this.userResponse = sessionData.userResponse;

      const remainingTime = this.getRemainingTimer();

      // Se não há tempo restante, limpa a sessão
      if (remainingTime <= 0) {
        this.clearPasswordResetSession();
        // Não abre o modal, usuário terá que iniciar o processo novamente
        return;
      }

      // Se há tempo restante, abre o modal direto na etapa de código
      this.initialTimerValue = remainingTime;
      this.showValidationModal = true;

      // Aguarda o modal estar montado para avançar para a etapa de código
      setTimeout(() => {
        if (this.$refs.modalValidate) {
          // Define o canal selecionado no modal
          if (this.selectedChannel) {
            this.$refs.modalValidate.selectedChannel = this.selectedChannel;
          }
          // Avança para a etapa de código com o timer restante
          this.$refs.modalValidate.proceedToCodeInput();
        }
      }, 100);
    },

    getRemainingTimer() {
      try {
        const savedTimestamp = localStorage.getItem(
          "passwordResetTokenTimestamp"
        );

        if (!savedTimestamp) {
          return 0;
        }

        const timestamp = parseInt(savedTimestamp, 10);
        if (isNaN(timestamp) || timestamp <= 0) {
          localStorage.removeItem("passwordResetTokenTimestamp");
          return 0;
        }

        const now = Date.now();
        if (isNaN(now) || now < timestamp) {
          localStorage.removeItem("passwordResetTokenTimestamp");
          return 0;
        }

        const elapsed = Math.floor((now - timestamp) / 1000);
        if (elapsed < 0) {
          return 0;
        }

        const remaining = 60 - elapsed;
        return remaining > 0 ? remaining : 0;
      } catch (err) {
        localStorage.removeItem("passwordResetTokenTimestamp");
        return 0;
      }
    },

    validateCode(code) {
      if (!code || typeof code !== "string") {
        return false;
      }
      const cleaned = code.replace(/\D/g, "");
      return cleaned.length === 6 && /^\d{6}$/.test(cleaned);
    },

    onCodeValidate(code) {
      if (this.isProcessing) {
        return;
      }

      if (!code || !this.validateCode(code)) {
        if (this.$refs.modalValidate) {
          this.$refs.modalValidate.setError(
            "Código inválido. Digite 6 dígitos numéricos."
          );
          this.$refs.modalValidate.setErrorState(true);
        }
        return;
      }

      const moveMaisId = this.getMoveMaisIdFromResponse(this.userResponse);
      if (!moveMaisId) {
        if (this.$refs.modalValidate) {
          this.$refs.modalValidate.setError(
            "Erro: dados do usuário não encontrados."
          );
        }
        return;
      }
      const moveMaisIdNum = Number(moveMaisId);

      if (isNaN(moveMaisIdNum) || moveMaisIdNum <= 0) {
        if (this.$refs.modalValidate) {
          this.$refs.modalValidate.setError("Erro: ID do usuário inválido.");
        }
        return;
      }

      const sanitizedCode = String(code).replace(/\D/g, "").substring(0, 6);

      this.isProcessing = true;

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("Tempo de validação excedido. Tente novamente."));
        }, this.requestTimeout);
      });

      Promise.race([
        auth.validarTokenPorMoveMaisId(moveMaisIdNum, sanitizedCode),
        timeoutPromise,
      ])
        .then((response) => {
          this.isProcessing = false;

          if (!response || typeof response !== "object") {
            throw new Error("Resposta inválida do servidor");
          }

          const statusCode =
            response && response.status && response.status.code;

          if (
            statusCode === 0 ||
            statusCode === undefined ||
            statusCode === null
          ) {
            this.processPasswordResetSuccess(moveMaisIdNum);
          } else {
            if (this.$refs.modalValidate) {
              const errorMsg =
                response.status && response.status.message
                  ? String(response.status.message).substring(0, 200)
                  : "Código inválido. Tente novamente.";
              this.$refs.modalValidate.setError(errorMsg);
              this.$refs.modalValidate.setErrorState(true);
            }
          }
        })
        .catch((err) => {
          this.isProcessing = false;

          let errorMessage = "Código inválido. Tente novamente.";

          if (err.message && err.message.includes("Tempo de validação")) {
            errorMessage = "Tempo de validação excedido. Tente novamente.";
          } else if (
            err.code === "ECONNABORTED" ||
            err.message === "Network Error"
          ) {
            errorMessage =
              "Erro de conexão. Verifique sua internet e tente novamente.";
          } else if (
            err.response &&
            err.response.data &&
            err.response.data.status &&
            err.response.data.status.message
          ) {
            errorMessage = String(err.response.data.status.message).substring(
              0,
              200
            );
          }

          if (this.$refs.modalValidate) {
            this.$refs.modalValidate.setError(errorMessage);
            this.$refs.modalValidate.setErrorState(true);
          }
        });
    },

    processPasswordResetSuccess(moveMaisId) {
      if (this.isProcessing) {
        return;
      }

      this.isProcessing = true;
      this.showValidationModal = false;

      try {
        // Limpa a sessão quando o reset é concluído com sucesso
        this.clearPasswordResetSession();
      } catch (err) {}

      const cpfSemMascara = this.taxIdNumber.replace(/[.\-\/]/g, "").toUpperCase();

      if (!cpfSemMascara || cpfSemMascara.length < 11) {
        this.formState = "ERROR";
        this.errorMessage = "CPF/CNPJ inválido";
        this.initProcess = false;
        this.isProcessing = false;
        return;
      }

      if (!moveMaisId || isNaN(Number(moveMaisId)) || Number(moveMaisId) <= 0) {
        this.formState = "ERROR";
        this.errorMessage = "ID do usuário inválido";
        this.initProcess = false;
        this.isProcessing = false;
        return;
      }

      // Verifica se o usuário tem 2FA habilitado
      if (
        this.userResponse &&
        this.userResponse.user &&
        this.userResponse.user.flag2FA === "Y"
      ) {
        const hash = window.location.hash;
        auth.saveHash(hash);
        this.formState = "NEW";
        this.errorMessage = undefined;
        this.$validator.validateAll().then((success) => {
          const taxId = this.taxIdNumber.replace(/[.\-\/]/g, "").toUpperCase();
          auth.saveTaxIdNumber(taxId);
          return auth
            .validUserResetPassword(taxId)
            .then((res) => {
              auth.saveMoveMaisId(res.moveMaisId);
              this.$router.push({
                name: "2fa-login",
                params: { type: "RESET_PASSWORD" },
              });
            })
            .catch((err) => {
              console.error(err);
              this.errorMessage =
                err.data && err.data.status
                  ? err.data.status.message
                  : "Erro ao processar.";
              this.isProcessing = false;
            });
        });
        return;
      }

      // Para usuários sem 2FA, chama diretamente o newRequestPasswordReset
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("Tempo de requisição excedido"));
        }, this.requestTimeout);
      });

      Promise.race([
        auth.newRequestPasswordReset(cpfSemMascara, Number(moveMaisId)),
        timeoutPromise,
      ])
        .then(() => {
          this.isProcessing = false;
          this.formState = "SUCCESS";
          this.errorMessage = undefined;
          this.initProcess = false;
        })
        .catch((err) => {
          this.isProcessing = false;
          this.formState = "ERROR";

          if (err.message && err.message.includes("Tempo de requisição")) {
            this.errorMessage =
              "Tempo de requisição excedido. Tente novamente.";
          } else if (
            err.code === "ECONNABORTED" ||
            err.message === "Network Error"
          ) {
            this.errorMessage =
              "Erro de conexão. Verifique sua internet e tente novamente.";
          } else {
            this.errorMessage =
              (err.response &&
                err.response.data &&
                err.response.data.status &&
                err.response.data.status.message) ||
              "Erro ao processar. Tente novamente.";
          }
          this.initProcess = false;
        });
    },
    onResendCode() {
      if (this.isProcessing) {
        return;
      }

      // Limpa o canal selecionado para permitir nova seleção
      this.selectedChannel = null;
      // Limpa o timestamp do timer para permitir novo envio
      try {
        localStorage.removeItem("passwordResetTokenTimestamp");
      } catch (err) {}

      // Salva a sessão sem o canal selecionado
      this.savePasswordResetSession();

      // Volta para a etapa de seleção de canal
      if (this.$refs.modalValidate) {
        this.$refs.modalValidate.resetToChannelSelection();
      }
    },

    onChannelSelected(channel) {
      if (this.isProcessing) {
        return;
      }

      if (!this.pendingMoveMaisId) {
        if (this.$refs.modalValidate) {
          this.$refs.modalValidate.resetToChannelSelection();
          this.$refs.modalValidate.channelErrorMessage =
            "Erro: dados do usuário não encontrados.";
        }
        return;
      }

      this.selectedChannel = channel;

      // Salva a sessão com o canal selecionado
      this.savePasswordResetSession();

      this.isProcessing = true;

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("Tempo de requisição excedido"));
        }, this.requestTimeout);
      });

      Promise.race([
        auth.generateTokenSite(this.pendingMoveMaisId, channel),
        timeoutPromise,
      ])
        .then((tokenResponse) => {
          this.isProcessing = false;

          if (!tokenResponse || typeof tokenResponse !== "object") {
            throw new Error("Resposta inválida ao gerar token");
          }

          const statusCode =
            tokenResponse && tokenResponse.status && tokenResponse.status.code;

          if (statusCode === 9990) {
            // Token já foi gerado - mantém o timer existente e avança para código
            const remainingTime = this.getRemainingTimer();
            this.initialTimerValue = remainingTime > 0 ? remainingTime : 0;

            // Avança para a etapa de código mantendo o timer
            if (this.$refs.modalValidate) {
              this.$refs.modalValidate.proceedToCodeInput();
            }
          } else if (
            statusCode === 0 ||
            statusCode === undefined ||
            statusCode === null
          ) {
            // Token gerado com sucesso - salva timestamp e inicia novo timer
            this.saveTokenTimestamp();
            this.initialTimerValue = 60;

            // Avança para a etapa de código
            if (this.$refs.modalValidate) {
              this.$refs.modalValidate.proceedToCodeInput();
            }
          } else {
            // Outro código de erro - trata como erro
            throw new Error(
              tokenResponse.status && tokenResponse.status.message
                ? tokenResponse.status.message
                : "Erro ao gerar token"
            );
          }
        })
        .catch((err) => {
          this.isProcessing = false;

          // Verifica se é erro 9990 (token já gerado)
          const errorCode =
            err.response &&
            err.response.data &&
            err.response.data.status &&
            err.response.data.status.code;

          if (errorCode === 9990) {
            // Token já foi gerado - mantém o timer existente e avança para código
            const remainingTime = this.getRemainingTimer();
            this.initialTimerValue = remainingTime > 0 ? remainingTime : 0;

            // Avança para a etapa de código mantendo o timer
            if (this.$refs.modalValidate) {
              this.$refs.modalValidate.proceedToCodeInput();
            }
          } else {
            // Outro tipo de erro - mostra mensagem e volta para seleção de canal
            let errorMessage = "Erro ao enviar código. Tente novamente.";

            if (err.message && err.message.includes("Tempo de requisição")) {
              errorMessage = "Tempo de requisição excedido. Tente novamente.";
            } else if (
              err.code === "ECONNABORTED" ||
              err.message === "Network Error"
            ) {
              errorMessage =
                "Erro de conexão. Verifique sua internet e tente novamente.";
            } else if (
              err.response &&
              err.response.data &&
              err.response.data.status &&
              err.response.data.status.message
            ) {
              errorMessage = err.response.data.status.message;
            }

            if (this.$refs.modalValidate) {
              this.$refs.modalValidate.resetToChannelSelection();
              this.$refs.modalValidate.channelErrorMessage = errorMessage;
            }
          }
        });
    },

    closeValidationModal() {
      this.showValidationModal = false;
      this.initProcess = false;
      // Não limpa os dados aqui para permitir que o usuário volte
      // Os dados serão limpos quando o processo for concluído ou o timer expirar
    },
  },
};



// WEBPACK FOOTER //
// ./src/pages/auth/password-request.js